import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { LockKeyhole } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const AUTH_BG = "https://images.unsplash.com/photo-1628012209120-d9db7abf7eab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBtaW5pbWFsJTIwaG91c2UlMjBleHRlcmlvcnxlbnwwfHx8fDE3ODQ4NzAyNTF8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@kontrakan.id");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user && user !== false) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email.trim(), password);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
      return;
    }
    toast.success(`Selamat datang, ${res.user.name}`);
    navigate("/");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left visual */}
      <div className="relative hidden lg:block">
        <img
          src={AUTH_BG}
          alt="Modern house"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/60 mix-blend-multiply" />
        <div className="relative z-10 flex flex-col justify-between h-full p-10 text-primary-foreground">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg bg-white/15 backdrop-blur grid place-items-center">
              <BrandLogo className="h-7 w-7 text-white" />
            </div>
            <div>
              <div className="font-display text-lg font-semibold">Aplikasi Kontrakan Surbakti</div>
              <div className="text-[11px] uppercase tracking-[0.2em] opacity-80">Operational Suite</div>
            </div>
          </div>
          <div className="max-w-md">
            <h1 className="font-display text-4xl xl:text-5xl font-semibold tracking-tight leading-tight">
              Kelola kontrakan.
              <br />
              <span className="opacity-70">Tanpa ribet, dari lapangan.</span>
            </h1>
            <p className="mt-6 text-sm/relaxed opacity-80">
              Tagihan otomatis, pembayaran bertahap, denda telat, dan kwitansi cetak — semua di
              satu tempat, dari HP maupun laptop.
            </p>
          </div>
          <div className="text-xs uppercase tracking-[0.2em] opacity-60">
            © {new Date().getFullYear()} · Aplikasi Kontrakan Surbakti
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-11 w-11 rounded-lg bg-primary grid place-items-center">
              <BrandLogo className="h-7 w-7 text-primary-foreground" />
            </div>
            <div>
              <div className="font-display text-lg font-semibold">Aplikasi Kontrakan Surbakti</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Operational Suite
              </div>
            </div>
          </div>
          <div className="mb-8">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-3">
              Masuk ke akun Anda
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              Selamat kembali.
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Gunakan kredensial admin/pemilik untuk melanjutkan.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="h-11"
              />
            </div>

            {error && (
              <div
                className="text-sm text-destructive rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-11 gap-2 transition-colors duration-200 active:scale-[0.99]"
              disabled={loading}
              data-testid="login-submit-button"
            >
              <LockKeyhole className="h-4 w-4" />
              {loading ? "Memproses..." : "Masuk"}
            </Button>
          </form>

          <div className="mt-8 text-xs text-muted-foreground">
            Aplikasi internal — hubungi pemilik jika belum punya akses.
          </div>
        </div>
      </div>
    </div>
  );
}
